/**
 * QueryState — one consistent wrapper for loading / error / empty data states.
 *
 * Every data-driven surface should render through this so a failed fetch
 * never silently looks like "no data". Copy is intentionally plain language.
 *
 * Usage:
 *   <QueryState
 *     isLoading={isLoading}
 *     error={error}
 *     isEmpty={!items.length}
 *     onRetry={refetch}
 *     emptyTitle="No trails yet"
 *   >
 *     {items.map(...)}
 *   </QueryState>
 */
import React from "react";
import { AlertTriangle, RefreshCw, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LoadingState } from "@/components/common/LoadingState";
import { EmptyState } from "@/components/common/EmptyState";
import { cn } from "@/lib/utils";

interface QueryStateProps {
  isLoading?: boolean;
  /** Any error value from react-query / try-catch. */
  error?: unknown;
  /** True when the fetch succeeded but returned nothing. */
  isEmpty?: boolean;
  /** Called by the "Try again" button. */
  onRetry?: () => void;
  children: React.ReactNode;

  /** Loading presentation: spinner (default) or skeleton cards. */
  loadingVariant?: "spinner" | "skeleton";
  /** Number of skeleton rows when loadingVariant is "skeleton". */
  skeletonCount?: number;
  loadingMessage?: string;

  errorTitle?: string;
  errorDescription?: string;

  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  className?: string;
}

/** Turns any thrown value into a short, non-technical sentence. */
export function friendlyErrorMessage(error: unknown): string {
  const raw =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  const text = raw.toLowerCase();

  if (!navigator.onLine || text.includes("failed to fetch") || text.includes("network")) {
    return "You seem to be offline. Check your connection and try again.";
  }
  if (text.includes("jwt") || text.includes("401") || text.includes("not authenticated")) {
    return "Your session expired. Please sign in again to continue.";
  }
  if (text.includes("permission") || text.includes("row-level security") || text.includes("403")) {
    return "You don't have access to this yet.";
  }
  if (text.includes("timeout") || text.includes("timed out")) {
    return "This is taking longer than usual. Please try again.";
  }
  return "We couldn't load this right now. Please try again.";
}

export function QueryErrorState({
  error,
  onRetry,
  title = "Something went wrong",
  description,
  className,
}: {
  error?: unknown;
  onRetry?: () => void;
  title?: string;
  description?: string;
  className?: string;
}) {
  const offline = typeof navigator !== "undefined" && !navigator.onLine;
  const Icon = offline ? WifiOff : AlertTriangle;

  return (
    <Card
      className={cn("border-dashed border-destructive/30 bg-destructive/5", className)}
      role="alert"
    >
      <div className="flex flex-col items-center justify-center gap-3 py-10 px-6 text-center">
        <div className="rounded-full bg-destructive/10 p-3">
          <Icon className="h-6 w-6 text-destructive" />
        </div>
        <div className="space-y-1">
          <h3 className="text-base font-semibold">{offline ? "You're offline" : title}</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            {description ?? friendlyErrorMessage(error)}
          </p>
        </div>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry} className="mt-1 gap-2">
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
        )}
      </div>
    </Card>
  );
}

export function QueryState({
  isLoading,
  error,
  isEmpty,
  onRetry,
  children,
  loadingVariant = "spinner",
  skeletonCount = 3,
  loadingMessage = "Loading...",
  errorTitle,
  errorDescription,
  emptyTitle,
  emptyDescription,
  emptyAction,
  className,
}: QueryStateProps) {
  // Errors win over stale/empty data so failures are never mistaken for "nothing here".
  if (error) {
    return (
      <QueryErrorState
        error={error}
        onRetry={onRetry}
        title={errorTitle}
        description={errorDescription}
        className={className}
      />
    );
  }

  if (isLoading) {
    if (loadingVariant === "skeleton") {
      return (
        <div className={cn("space-y-3", className)} aria-busy="true">
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      );
    }
    return <LoadingState inline message={loadingMessage} className={className} />;
  }

  if (isEmpty && emptyTitle) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
        className={className}
      />
    );
  }

  return <>{children}</>;
}
